import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './services/AuthContext';
import { ConfigProvider } from './context/ConfigProvider';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CurrencyProvider } from './services/CurrencyContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { CountryProvider, useCountry } from './context/CountryContext';
import { VerifyAgentProvider } from './context/VerifyAgentContext';
import ScrollToTop from './components/ScrollToTop';
import MonitoringProvider from './context/MonitoringProvider';
import ERealtyLayout from './layouts/ERealtyLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { useAutoAuth } from './services/AuthContext';

// --- Shared Components ---
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-[#0A0A0A]">
    <LoadingSpinner label="Calibrating Trinity Sync" />
  </div>
);

// --- 1. Consumer Canvas Node ---
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const ERealtyPage = lazy(() => import('./pages/ERealtyPage'));
const PartnerPropertyDetailPage = lazy(() => import('./pages/PartnerPropertyDetailPage'));
const PartnerCompoundDetailPage = lazy(() => import('./pages/PartnerCompoundDetailPage'));
const PartnerLoginPage = lazy(() => import('./pages/PartnerLoginPage'));
const PartnerSignupPage = lazy(() => import('./pages/PartnerSignupPage'));

// --- Shared Auth Logic ---
const ProtectedRoute = () => {
    const { isLoggedIn, isLoading } = useAutoAuth();
    const { activeCountry } = useCountry();
    
    if (isLoading) return <PageLoader />;
    
    if (!isLoggedIn) {
        // Explicitly go to localized erealty login
        const cc = activeCountry?.code?.toLowerCase() || 'eg';
        return <Navigate to={`/${cc}/erealty/login`} replace />;
    }
    
    return <Outlet />;
};

const RootRedirect = () => {
    const { activeCountry } = useCountry();
    if (!activeCountry) return <PageLoader />;
    return <Navigate to={`/${activeCountry.code.toLowerCase()}`} replace />;
};

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <MonitoringProvider>
      <HelmetProvider>
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID_HERE"}>
          <AuthProvider>
            <AuthModalProvider>
              <VerifyAgentProvider>
                <ConfigProvider>
                  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <CountryProvider>
                      <CurrencyProvider>
                        <ScrollToTop />
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<RootRedirect />} />
                            
                            <Route path="/:countryCode">
                              <Route index element={<Navigate to="erealty/dashboard" replace />} />
                              <Route path="erealty/login" element={<PartnerLoginPage />} />
                              <Route path="erealty/signup" element={<PartnerSignupPage />} />
                              
                              <Route element={<ProtectedRoute />}>
                                <Route path="erealty" element={<ERealtyLayout />}>
                                  <Route path="dashboard" element={<ERealtyPage />} />
                                  <Route path="inventory" element={<InventoryPage />} />
                                  <Route path="favorites" element={<FavoritesPage />} />
                                  <Route path="property/:id" element={<PartnerPropertyDetailPage />} />
                                  <Route path="compound/:id" element={<PartnerCompoundDetailPage />} />
                                  <Route path="" element={<Navigate to="dashboard" replace />} />
                                </Route>
                              </Route>
                            </Route>
                            
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Suspense>
                      </CurrencyProvider>
                    </CountryProvider>
                  </Router>
                </ConfigProvider>
              </VerifyAgentProvider>
            </AuthModalProvider>
          </AuthProvider>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </MonitoringProvider>
  );
}

export default App;
